import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Popup from "../../../components/Popup";
import {
    FinancePageContainer,
    FinancePageHeader,
    FinanceHeadCell,
    FinanceTableRow,
    financeTableContainerSx,
} from "./financeUi";
import {
    getAllFeeStructures,
    createFeeStructure,
    updateFeeStructure,
    assignFeeToClass,
    syncFeeAssignments,
    deleteFeeStructure,
} from "../../../redux/feeRelated/feeHandle";
import { underFeeControl } from "../../../redux/feeRelated/feeSlice";
import { getAllSclasses } from "../../../redux/sclassRelated/sclassHandle";
import { matchesSecondaryClass, matchesPrimaryClass } from "../../../utils/classScopeMatch";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { isBursarUser } from "../../../utils/staffRoles";

const FEE_TYPES = [
    "Tuition",
    "PTA Levy",
    "Development Levy",
    "Exam Fee",
    "Boarding",
    "Transport",
    "Library",
    "Sports",
    "Uniform",
    "Other",
];
const TERMS = ["Term 1", "Term 2", "Term 3", "Full Year"];
const y = new Date().getFullYear();
const YEARS = [`${y - 1}/${y}`, `${y}/${y + 1}`, `${y + 1}/${y + 2}`];
const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();

const nextItemKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

function feeErrorMessage(err) {
    if (!err) return "Request failed";
    if (typeof err === "string") return err;
    if (typeof err.response?.data?.message === "string") return err.response.data.message;
    if (err.message) return err.message;
    return "Request failed";
}

function defaultClassScope(schoolType) {
    if (schoolType === "Primary") return "all_primary";
    if (schoolType === "Secondary") return "all_secondary";
    if (schoolType === "Combined") return "all_secondary";
    return "single";
}

const FeeStructureManagement = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { feeStructures, loading, response, error, statestatus, successMessage } = useSelector((s) => s.fee);
    const { sclassesList } = useSelector((s) => s.sclass);
    const schoolId = getSchoolIdForUser(currentUser);
    const isBursarPortal = isBursarUser(currentUser);
    const canApplyFeesToStudents = currentUser?.role === "Admin";

    const [showPopup, setShowPopup] = useState(false);
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [message, setMessage] = useState("");

    const [structureDialogOpen, setStructureDialogOpen] = useState(false);
    const [structureDialogMode, setStructureDialogMode] = useState("create");
    const [editingId, setEditingId] = useState(null);
    const [editIsActive, setEditIsActive] = useState(true);
    const [assignOpen, setAssignOpen] = useState(false);
    const [selectedStructure, setSelectedStructure] = useState(null);

    const [classScope, setClassScope] = useState(() => defaultClassScope(currentUser?.schoolType || "Secondary"));
    const [formClassId, setFormClassId] = useState("");
    const [formTerm, setFormTerm] = useState("");
    const [formYear, setFormYear] = useState("");
    const [feeItemRows, setFeeItemRows] = useState([
        { key: nextItemKey(), feeType: "", amount: "", description: "" },
    ]);
    const [useInstallments, setUseInstallments] = useState(false);
    const [installmentRows, setInstallmentRows] = useState([
        { key: nextItemKey(), label: "Installment 1", dueDate: "", amount: "" },
        { key: nextItemKey(), label: "Installment 2", dueDate: "", amount: "" },
        { key: nextItemKey(), label: "Installment 3", dueDate: "", amount: "" },
    ]);

    const structures = useMemo(
        () => (Array.isArray(feeStructures) ? feeStructures : []),
        [feeStructures]
    );

    const schoolType = currentUser?.schoolType || "Secondary";
    const isSecondarySchool = schoolType === "Secondary" || schoolType === "Combined";

    const hasTuitionInForm = useMemo(
        () => feeItemRows.some((r) => String(r.feeType || "").trim().toLowerCase() === "tuition"),
        [feeItemRows]
    );

    const showPrimaryBulk = schoolType === "Primary" || schoolType === "Combined";
    const showSecondaryBulk = schoolType === "Secondary" || schoolType === "Combined";

    const classScopeOptions = useMemo(() => {
        const opts = [{ value: "single", label: "One class only" }];
        if (showSecondaryBulk) {
            opts.push({
                value: "all_secondary",
                label: "All secondary classes (Form 1 – Form 4)",
            });
        }
        if (showPrimaryBulk) {
            opts.push({
                value: "all_primary",
                label: "All primary classes (Std 1 – Std 8)",
            });
        }
        if (schoolType === "Combined") {
            opts.push({
                value: "all_school",
                label: "All classes (primary + secondary)",
            });
        }
        return opts;
    }, [showPrimaryBulk, showSecondaryBulk, schoolType]);

    const targetClassesPreview = useMemo(() => {
        const list = Array.isArray(sclassesList) ? sclassesList : [];
        if (classScope === "single") {
            const one = list.find((c) => (c._id || c.id) === formClassId);
            return one ? [one] : [];
        }
        if (classScope === "all_secondary") {
            return list.filter((c) => matchesSecondaryClass(c));
        }
        if (classScope === "all_primary") {
            return list.filter((c) => matchesPrimaryClass(c));
        }
        if (classScope === "all_school") return list;
        return [];
    }, [classScope, formClassId, sclassesList]);

    const refreshLists = useCallback(() => {
        if (!schoolId) return;
        dispatch(getAllFeeStructures(schoolId));
        dispatch(getAllSclasses(schoolId, "Sclass"));
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (!schoolId) return;
        dispatch(getAllFeeStructures(schoolId));
        dispatch(getAllSclasses(schoolId, "Sclass"));
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (statestatus === "added") {
            const msg =
                successMessage ||
                "Fee structure saved successfully. Students in the affected classes are billed automatically on registration.";
            setMessage(msg);
            if (
                /already have a fee structure|already exist|skipped/i.test(msg) &&
                !/successfully created [1-9]/i.test(msg)
            ) {
                setPopupSeverity("warning");
            } else {
                setPopupSeverity("success");
            }
            setShowPopup(true);
            dispatch(underFeeControl());
            refreshLists();
            setStructureDialogOpen(false);
            setStructureDialogMode("create");
            setEditingId(null);
            setAssignOpen(false);
            setSelectedStructure(null);
        } else if (statestatus === "failed") {
            const msg =
                typeof response === "string" && response
                    ? response
                    : feeErrorMessage(error);
            setMessage(msg || "Request failed");
            setPopupSeverity("error");
            setShowPopup(true);
            dispatch(underFeeControl());
        }
    }, [statestatus, response, error, dispatch, refreshLists, successMessage]);

    const formTotal = useMemo(() => {
        return feeItemRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    }, [feeItemRows]);

    const resetCreateForm = () => {
        setClassScope(defaultClassScope(schoolType));
        setFormClassId("");
        setFormTerm("");
        setFormYear("");
        setFeeItemRows([{ key: nextItemKey(), feeType: "", amount: "", description: "" }]);
        setUseInstallments(false);
        setInstallmentRows([
            { key: nextItemKey(), label: "Installment 1", dueDate: "", amount: "" },
            { key: nextItemKey(), label: "Installment 2", dueDate: "", amount: "" },
            { key: nextItemKey(), label: "Installment 3", dueDate: "", amount: "" },
        ]);
    };

    const closeStructureDialog = () => {
        setStructureDialogOpen(false);
        setStructureDialogMode("create");
        setEditingId(null);
    };

    /** Same as closing the create/edit dialog (useState-style API for legacy JSX / linters). */
    const setCreateOpen = (open) => {
        if (open) setStructureDialogOpen(true);
        else closeStructureDialog();
    };

    const handleOpenCreate = () => {
        resetCreateForm();
        setStructureDialogMode("create");
        setEditingId(null);
        setEditIsActive(true);
        if (schoolId) {
            dispatch(getAllSclasses(schoolId, "Sclass"));
        }
        setStructureDialogOpen(true);
    };

    useEffect(() => {
        if (structureDialogOpen && schoolId) {
            dispatch(getAllSclasses(schoolId, "Sclass"));
        }
    }, [structureDialogOpen, schoolId, dispatch]);

    const handleOpenEdit = (row) => {
        const rid = row.id || row._id;
        const items = Array.isArray(row.feeItems) ? row.feeItems : [];
        setStructureDialogMode("edit");
        setEditingId(rid);
        setEditIsActive(Boolean(row.isActive));
        setFormClassId(row.sclassId || row.sclass?.id || "");
        setFormTerm(row.term || "");
        setFormYear(row.academicYear || "");
        setFeeItemRows(
            items.length
                ? items.map((it) => ({
                      key: nextItemKey(),
                      feeType: it.feeType || "",
                      amount: String(it.amount ?? ""),
                      description: it.description || "",
                  }))
                : [{ key: nextItemKey(), feeType: "", amount: "", description: "" }]
        );
        const inst = Array.isArray(row.installments) ? row.installments : [];
        setUseInstallments(inst.length > 0);
        setInstallmentRows(
            inst.length
                ? inst.map((it, i) => ({
                      key: nextItemKey(),
                      label: it.label || `Installment ${i + 1}`,
                      dueDate: it.dueDate ? String(it.dueDate).slice(0, 10) : "",
                      amount: String(it.amount ?? ""),
                  }))
                : [
                      { key: nextItemKey(), label: "Installment 1", dueDate: "", amount: "" },
                      { key: nextItemKey(), label: "Installment 2", dueDate: "", amount: "" },
                      { key: nextItemKey(), label: "Installment 3", dueDate: "", amount: "" },
                  ]
        );
        setStructureDialogOpen(true);
    };

    const handleAddFeeRow = () => {
        setFeeItemRows((rows) => [
            ...rows,
            { key: nextItemKey(), feeType: "", amount: "", description: "" },
        ]);
    };

    const handleRemoveFeeRow = (key) => {
        setFeeItemRows((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.key !== key)));
    };

    const handleFeeRowChange = (key, field, value) => {
        setFeeItemRows((rows) =>
            rows.map((r) => (r.key === key ? { ...r, [field]: value } : r))
        );
    };

    const buildFeeItemsPayload = () =>
        feeItemRows
            .map((r) => ({
                feeType: String(r.feeType || "").trim(),
                amount: Number(r.amount) || 0,
                description: String(r.description || "").trim(),
                isOptional: false,
            }))
            .filter((it) => it.feeType && it.amount > 0);

    const buildInstallmentsPayload = () => {
        if (!useInstallments) return [];
        return installmentRows
            .map((row, i) => ({
                installmentNo: i + 1,
                label: String(row.label || `Installment ${i + 1}`).trim(),
                dueDate: row.dueDate || null,
                amount: Number(row.amount) || 0,
            }))
            .filter((it) => it.amount > 0);
    };

    const handleCreateSubmit = () => {
        if (!schoolId) return;
        const feeItems = buildFeeItemsPayload();

        if (structureDialogMode === "create") {
            if (!formTerm || !formYear || feeItems.length === 0) {
                setMessage("Please fill term, academic year, and at least one fee item with type and amount.");
                setShowPopup(true);
                return;
            }
            if (classScope === "single" && !formClassId) {
                setMessage("Please select a class, or choose “All secondary” / “All primary” to apply to every class at once.");
                setShowPopup(true);
                return;
            }
            if (classScope !== "single" && targetClassesPreview.length === 0) {
                setMessage(
                    "No classes match this scope. Create Form 1–4 or Std 1–8 classes first, then try again."
                );
                setShowPopup(true);
                return;
            }
            dispatch(
                createFeeStructure({
                    schoolId,
                    classScope,
                    sclassId: classScope === "single" ? formClassId : undefined,
                    academicYear: formYear,
                    term: formTerm,
                    feeItems,
                    installments: buildInstallmentsPayload(),
                })
            );
            return;
        }

        if (!editingId || feeItems.length === 0) {
            setMessage("Add at least one fee item with type and amount.");
            setShowPopup(true);
            return;
        }
        dispatch(
            updateFeeStructure(editingId, {
                feeItems,
                academicYear: formYear,
                term: formTerm,
                isActive: editIsActive,
                installments: buildInstallmentsPayload(),
            })
        );
    };

    const handleAssignSubmit = () => {
        if (!schoolId || !selectedStructure) return;
        const fid = selectedStructure.id || selectedStructure._id;
        dispatch(
            assignFeeToClass({
                feeStructureId: fid,
                schoolId,
            })
        );
    };

    const handleDelete = (structure) => {
        const sid = structure.id || structure._id;
        if (
            !window.confirm(
                "Deactivate this fee structure and remove unpaid fee records for students who have not paid?"
            )
        ) {
            return;
        }
        dispatch(deleteFeeStructure(sid));
        setTimeout(() => {
            if (schoolId) dispatch(getAllFeeStructures(schoolId));
        }, 600);
    };

    const openAssign = (row) => {
        setSelectedStructure(row);
        setAssignOpen(true);
    };

    const classLabel = (row) => row.sclass?.sclassName ?? "—";

    return (
        <FinancePageContainer maxWidth="lg">
            <Popup
                message={message}
                setShowPopup={setShowPopup}
                showPopup={showPopup}
                severity={popupSeverity}
            />

            {isBursarPortal ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    You can set up fee structures and collect payments. Only the school director can apply
                    structures to students (bill every learner) from the admin portal.
                </Alert>
            ) : null}

            <FinancePageHeader
                title="Fee structures"
                subtitle={`${structures.length} structure${
                    structures.length === 1 ? "" : "s"
                } for ${currentUser?.schoolName ?? "your school"}. Create for one class or all Form 1–4 / Std 1–8 at once.${
                    canApplyFeesToStudents
                        ? " After saving, use Apply fees to all students so every learner receives a bill."
                        : ""
                }`}
                action={
                    <Stack direction="row" spacing={1}>
                        {structures.length > 0 && canApplyFeesToStudents ? (
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<SyncIcon />}
                                onClick={() => dispatch(syncFeeAssignments(schoolId))}
                                disabled={!schoolId || loading}
                            >
                                Apply fees to all students
                            </Button>
                        ) : null}
                        <Button variant="contained" size="large" onClick={handleOpenCreate}>
                            New structure
                        </Button>
                    </Stack>
                }
            />

            <TableContainer component={Paper} elevation={0} sx={financeTableContainerSx}>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <FinanceHeadCell>Class</FinanceHeadCell>
                            <FinanceHeadCell>Term</FinanceHeadCell>
                            <FinanceHeadCell>Academic year</FinanceHeadCell>
                            <FinanceHeadCell>Fee items</FinanceHeadCell>
                            <FinanceHeadCell align="right">Total</FinanceHeadCell>
                            <FinanceHeadCell>Status</FinanceHeadCell>
                            <FinanceHeadCell align="right">Actions</FinanceHeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && structures.length === 0 ? (
                            <FinanceTableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">Loading structures…</Typography>
                                </TableCell>
                            </FinanceTableRow>
                        ) : structures.length === 0 ? (
                            <FinanceTableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary" gutterBottom>
                                        No fee structures yet.
                                    </Typography>
                                    <Button variant="outlined" size="small" onClick={handleOpenCreate}>
                                        Create your first structure
                                    </Button>
                                </TableCell>
                            </FinanceTableRow>
                        ) : (
                            structures.map((row) => {
                                const rid = row.id || row._id;
                                const items = Array.isArray(row.feeItems) ? row.feeItems : [];
                                return (
                                    <FinanceTableRow key={rid}>
                                        <TableCell sx={{ fontWeight: 600 }}>{classLabel(row)}</TableCell>
                                        <TableCell>{row.term ?? "—"}</TableCell>
                                        <TableCell>{row.academicYear ?? "—"}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
                                                {items.map((it) => {
                                                    const iid = it.id || it._id || `${it.feeType}-${it.amount}`;
                                                    return (
                                                        <Tooltip
                                                            key={iid}
                                                            title={formatMWK(it.amount)}
                                                            arrow
                                                        >
                                                            <Chip
                                                                size="small"
                                                                label={it.feeType || "Item"}
                                                                variant="outlined"
                                                                color="primary"
                                                            />
                                                        </Tooltip>
                                                    );
                                                })}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                                            {formatMWK(row.totalAmount)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.isActive ? "Active" : "Inactive"}
                                                color={row.isActive ? "success" : "default"}
                                                variant={row.isActive ? "filled" : "outlined"}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit fee items" arrow>
                                                <IconButton size="small" color="secondary" onClick={() => handleOpenEdit(row)}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {canApplyFeesToStudents ? (
                                                <Tooltip title="Apply this structure to all students in the class" arrow>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => openAssign(row)}
                                                        disabled={!row.isActive}
                                                    >
                                                        <GroupAddIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                            <Tooltip title="Deactivate structure" arrow>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(row)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </FinanceTableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={structureDialogOpen}
                onClose={() => setCreateOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle>
                    {structureDialogMode === "edit" ? "Edit fee structure" : "New fee structure"}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {structureDialogMode === "create" && classScope !== "single" && (
                            <Alert
                                severity={targetClassesPreview.length > 0 ? "success" : "warning"}
                                sx={{ borderRadius: 2 }}
                            >
                                <AlertTitle>Bulk apply</AlertTitle>
                                {targetClassesPreview.length > 0 ? (
                                    <>
                                        The same fee items will be created for{" "}
                                        <strong>
                                            {targetClassesPreview.length} class
                                            {targetClassesPreview.length === 1 ? "" : "es"}
                                        </strong>
                                        {classScope === "all_secondary" && " (Form 1 – Form 4)"}
                                        {classScope === "all_primary" && " (Std 1 – Std 8)"}
                                        . Existing structures for the same term are skipped.
                                    </>
                                ) : (
                                    <>
                                        No classes match this scope yet. Add classes under{" "}
                                        <strong>Classes</strong> (e.g. Form 1–4 with level codes or names like
                                        &quot;Form 1&quot;), then try again.
                                    </>
                                )}
                            </Alert>
                        )}
                        {isSecondarySchool &&
                            hasTuitionInForm &&
                            structureDialogMode === "create" &&
                            classScope === "single" && (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                <AlertTitle>Secondary school — tuition</AlertTitle>
                                Tuition is also copied to other secondary classes. To set fees for Form 1–4 in one step,
                                choose <strong>All secondary classes (Form 1 – Form 4)</strong> above.
                            </Alert>
                        )}
                        {isSecondarySchool && hasTuitionInForm && structureDialogMode === "edit" && (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                <AlertTitle>Secondary school — tuition</AlertTitle>
                                Updating tuition here also updates tuition on all other secondary classes for this term.
                            </Alert>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            {structureDialogMode === "create" ? (
                                <>
                                    One structure per class, term, and academic year. Put every charge (tuition, exam
                                    fee, levies, etc.) in <strong>Fee Items</strong> below — use &quot;Add Fee Item&quot;
                                    for each type.
                                </>
                            ) : (
                                <>
                                    Class, term, and academic year are fixed for this structure. Add or change fee
                                    types (tuition, exam fee, levies, etc.) below. If students already paid, edits may be
                                    blocked by the server.
                                </>
                            )}
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            {structureDialogMode === "create" && (
                                <FormControl fullWidth size="small">
                                    <InputLabel id="fee-scope-label">Apply to</InputLabel>
                                    <Select
                                        labelId="fee-scope-label"
                                        label="Apply to"
                                        value={classScope}
                                        onChange={(e) => setClassScope(e.target.value)}
                                    >
                                        {classScopeOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {(structureDialogMode === "edit" || classScope === "single") && (
                                <FormControl
                                    fullWidth
                                    size="small"
                                    disabled={structureDialogMode === "edit"}
                                >
                                    <InputLabel id="fee-class-label">Class</InputLabel>
                                    <Select
                                        labelId="fee-class-label"
                                        label="Class"
                                        value={formClassId}
                                        onChange={(e) => setFormClassId(e.target.value)}
                                    >
                                        {(Array.isArray(sclassesList) ? sclassesList : []).map((c) => {
                                            const cid = c._id || c.id;
                                            return (
                                                <MenuItem key={cid} value={cid}>
                                                    {c.sclassName}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            )}
                            <FormControl fullWidth size="small" disabled={structureDialogMode === "edit"}>
                                <InputLabel id="fee-term-label">Term</InputLabel>
                                <Select
                                    labelId="fee-term-label"
                                    label="Term"
                                    value={formTerm}
                                    onChange={(e) => setFormTerm(e.target.value)}
                                >
                                    {TERMS.map((t) => (
                                        <MenuItem key={t} value={t}>
                                            {t}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small" disabled={structureDialogMode === "edit"}>
                                <InputLabel id="fee-year-label">Academic year</InputLabel>
                                <Select
                                    labelId="fee-year-label"
                                    label="Academic year"
                                    value={formYear}
                                    onChange={(e) => setFormYear(e.target.value)}
                                >
                                    {YEARS.map((yr) => (
                                        <MenuItem key={yr} value={yr}>
                                            {yr}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        {structureDialogMode === "create" &&
                            classScope !== "single" &&
                            targetClassesPreview.length > 0 && (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {targetClassesPreview.map((c) => (
                                        <Chip
                                            key={c._id || c.id}
                                            size="small"
                                            label={c.sclassName}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            )}

                        {structureDialogMode === "edit" && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editIsActive}
                                        onChange={(e) => setEditIsActive(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Structure active (inactive structures cannot be assigned)"
                            />
                        )}

                        <Typography variant="subtitle2" color="text.secondary">
                            Fee Items
                        </Typography>

                        {feeItemRows.map((row) => (
                            <Stack
                                key={row.key}
                                direction={{ xs: "column", md: "row" }}
                                spacing={1}
                                alignItems={{ md: "center" }}
                            >
                                <FormControl size="small" sx={{ minWidth: { md: 160 }, flex: 1 }}>
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        label="Type"
                                        value={row.feeType}
                                        onChange={(e) =>
                                            handleFeeRowChange(row.key, "feeType", e.target.value)
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Select type</em>
                                        </MenuItem>
                                        {FEE_TYPES.map((ft) => (
                                            <MenuItem key={ft} value={ft}>
                                                {ft}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="Amount"
                                    value={row.amount}
                                    onChange={(e) =>
                                        handleFeeRowChange(row.key, "amount", e.target.value)
                                    }
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">MWK</InputAdornment>
                                        ),
                                    }}
                                    sx={{ width: { xs: "100%", md: 160 } }}
                                />
                                <TextField
                                    size="small"
                                    label="Description"
                                    value={row.description}
                                    onChange={(e) =>
                                        handleFeeRowChange(row.key, "description", e.target.value)
                                    }
                                    fullWidth
                                />
                                <IconButton
                                    aria-label="Remove fee row"
                                    onClick={() => handleRemoveFeeRow(row.key)}
                                    color="error"
                                    disabled={feeItemRows.length <= 1}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        ))}

                        <Button
                            startIcon={<AddIcon />}
                            variant="outlined"
                            size="small"
                            onClick={handleAddFeeRow}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            Add Fee Item
                        </Button>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={useInstallments}
                                    onChange={(e) => setUseInstallments(e.target.checked)}
                                />
                            }
                            label="Split bill into installments with due dates"
                        />

                        {useInstallments ? (
                            <Stack spacing={1}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Installment schedule
                                </Typography>
                                {installmentRows.map((row, idx) => (
                                    <Stack key={row.key} direction={{ xs: "column", sm: "row" }} spacing={1}>
                                        <TextField
                                            size="small"
                                            label="Label"
                                            value={row.label}
                                            onChange={(e) =>
                                                setInstallmentRows((prev) =>
                                                    prev.map((r) =>
                                                        r.key === row.key ? { ...r, label: e.target.value } : r
                                                    )
                                                )
                                            }
                                            sx={{ flex: 1 }}
                                        />
                                        <TextField
                                            size="small"
                                            label="Due date"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={row.dueDate}
                                            onChange={(e) =>
                                                setInstallmentRows((prev) =>
                                                    prev.map((r) =>
                                                        r.key === row.key ? { ...r, dueDate: e.target.value } : r
                                                    )
                                                )
                                            }
                                            sx={{ width: { sm: 180 } }}
                                        />
                                        <TextField
                                            size="small"
                                            label="Amount"
                                            type="number"
                                            value={row.amount}
                                            onChange={(e) =>
                                                setInstallmentRows((prev) =>
                                                    prev.map((r) =>
                                                        r.key === row.key ? { ...r, amount: e.target.value } : r
                                                    )
                                                )
                                            }
                                            sx={{ width: { sm: 140 } }}
                                        />
                                    </Stack>
                                ))}
                                <Typography variant="caption" color="text.secondary">
                                    Last installment adjusts to match each pupil&apos;s total (including boarding/day differences).
                                </Typography>
                            </Stack>
                        ) : null}

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 1,
                                bgcolor: "action.hover",
                                fontWeight: 600,
                            }}
                        >
                            Total: {formatMWK(formTotal)}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateSubmit}
                        disabled={
                            loading ||
                            (structureDialogMode === "create" &&
                                classScope !== "single" &&
                                targetClassesPreview.length === 0)
                        }
                    >
                        {structureDialogMode === "edit" ? "Save changes" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={assignOpen}
                onClose={() => setAssignOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle>Assign fees to class</DialogTitle>
                <DialogContent dividers>
                    {selectedStructure && (
                        <Stack spacing={2}>
                            <Typography variant="body2">
                                <strong>Class:</strong> {classLabel(selectedStructure)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Term:</strong> {selectedStructure.term ?? "—"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Academic year:</strong> {selectedStructure.academicYear ?? "—"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Total per student:</strong>{" "}
                                {formatMWK(selectedStructure.totalAmount)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                All students in this class will receive a fee record for this structure.
                                Students who already have a record for this structure are skipped
                                automatically.
                            </Typography>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAssignSubmit} disabled={loading}>
                        Assign to Class
                    </Button>
                </DialogActions>
            </Dialog>
        </FinancePageContainer>
    );
};

export default FeeStructureManagement;
